/**
 * Arbiter Sports TanStack Query hooks.
 *
 * Each query is keyed by `['arbiter', <area>, schoolPublicId, ...]` so the
 * Teams tab and Games tab can invalidate independently.
 *
 * Retry policy: never retry an `arbiter_auth` failure (the credentials are
 * broken — re-fetching won't help and the banner is already visible). For
 * `arbiter_unreachable`, retry up to twice; everything else: don't retry.
 */

import { useAuth } from '@clerk/nextjs'
import type {
  CreateArbiterTeamSectionLinkRequest,
  GetArbiterGamesRequest,
  UpdateArbiterTeamSectionLinkRequest
} from '@school-journey/shared-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  ArbiterApiError,
  createArbiterTeamSectionLink,
  deleteArbiterTeamSectionLink,
  getArbiterConfigured,
  getArbiterGames,
  getArbiterTeams,
  listArbiterSchoolSections,
  listArbiterTeamSectionLinks,
  updateArbiterTeamSectionLink,
  type ArbiterSchoolSection
} from '@/lib/api/arbiter'
import { useBackendClient } from '@/lib/backend-client'

const ROOT = ['arbiter'] as const

function arbiterRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ArbiterApiError) {
    if (error.kind === 'arbiter_auth' || error.kind === 'arbiter_not_configured') return false
    if (error.kind === 'arbiter_unreachable') return failureCount < 2
    return false
  }
  // Non-Arbiter (network blip, 5xx without envelope): one quick retry.
  return failureCount < 1
}

export function useArbiterConfigured(schoolPublicId: string | undefined) {
  const { userId } = useAuth()
  const client = useBackendClient()
  return useQuery({
    queryKey: [...ROOT, 'configured', schoolPublicId],
    queryFn: async () => {
      if (!schoolPublicId) throw new Error('schoolPublicId required')
      return getArbiterConfigured(client, schoolPublicId)
    },
    enabled: !!userId && !!schoolPublicId,
    staleTime: Infinity,
    retry: arbiterRetry
  })
}

export function useArbiterGames(schoolPublicId: string | undefined, params: GetArbiterGamesRequest) {
  const { userId } = useAuth()
  const client = useBackendClient()
  return useQuery({
    queryKey: [...ROOT, 'games', schoolPublicId, params],
    queryFn: async () => {
      if (!schoolPublicId) throw new Error('schoolPublicId required')
      return getArbiterGames(client, schoolPublicId, params)
    },
    enabled: !!userId && !!schoolPublicId,
    staleTime: 60 * 1000,
    retry: arbiterRetry
  })
}

export function useArbiterTeams(schoolPublicId: string | undefined) {
  const { userId } = useAuth()
  const client = useBackendClient()
  return useQuery({
    queryKey: [...ROOT, 'teams', schoolPublicId],
    queryFn: async () => {
      if (!schoolPublicId) throw new Error('schoolPublicId required')
      return getArbiterTeams(client, schoolPublicId)
    },
    enabled: !!userId && !!schoolPublicId,
    staleTime: 5 * 60 * 1000,
    retry: arbiterRetry
  })
}

export function useArbiterTeamSectionLinks(schoolPublicId: string | undefined) {
  const { userId } = useAuth()
  const client = useBackendClient()
  return useQuery({
    queryKey: [...ROOT, 'links', schoolPublicId],
    queryFn: async () => {
      if (!schoolPublicId) throw new Error('schoolPublicId required')
      return listArbiterTeamSectionLinks(client, schoolPublicId)
    },
    enabled: !!userId && !!schoolPublicId,
    staleTime: 5 * 60 * 1000,
    retry: arbiterRetry
  })
}

export function useLinkArbiterTeam(schoolPublicId: string | undefined) {
  const client = useBackendClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateArbiterTeamSectionLinkRequest) => {
      if (!schoolPublicId) throw new Error('schoolPublicId required')
      return createArbiterTeamSectionLink(client, schoolPublicId, input)
    },
    // The school is captured at mutate time: a school switch before the write
    // settles must refresh the school it was made in, not the new one.
    onMutate: () => ({ schoolPublicId }),
    onSuccess: (_data, _input, context) => {
      qc.invalidateQueries({ queryKey: [...ROOT, 'links', context.schoolPublicId] })
    }
  })
}

export function useUpdateArbiterTeamLink(schoolPublicId: string | undefined) {
  const client = useBackendClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateArbiterTeamSectionLinkRequest & { linkPublicId: string }) => {
      if (!schoolPublicId) throw new Error('schoolPublicId required')
      const { linkPublicId, ...body } = input
      return updateArbiterTeamSectionLink(client, schoolPublicId, linkPublicId, body)
    },
    onMutate: () => ({ schoolPublicId }),
    onSuccess: (_data, _input, context) => {
      qc.invalidateQueries({ queryKey: [...ROOT, 'links', context.schoolPublicId] })
    }
  })
}

export type { ArbiterSchoolSection }

/**
 * Lightweight section list for the team-section picker, narrowed to the fields
 * the picker uses (`listArbiterSchoolSections`).
 */
export function useArbiterSchoolSections(schoolPublicId: string | undefined) {
  const { userId } = useAuth()
  const client = useBackendClient()
  return useQuery({
    queryKey: [...ROOT, 'school-sections', schoolPublicId],
    queryFn: async (): Promise<ArbiterSchoolSection[]> => {
      if (!schoolPublicId) throw new Error('schoolPublicId required')
      return listArbiterSchoolSections(client, schoolPublicId)
    },
    enabled: !!userId && !!schoolPublicId,
    staleTime: 5 * 60 * 1000
  })
}

export function useUnlinkArbiterTeam(schoolPublicId: string | undefined) {
  const client = useBackendClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (linkPublicId: string) => {
      if (!schoolPublicId) throw new Error('schoolPublicId required')
      return deleteArbiterTeamSectionLink(client, schoolPublicId, linkPublicId)
    },
    onMutate: () => ({ schoolPublicId }),
    onSuccess: (_data, _input, context) => {
      qc.invalidateQueries({ queryKey: [...ROOT, 'links', context.schoolPublicId] })
    }
  })
}
